import * as React from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogOverlay,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogCloseButton,
  HStack,
  Checkbox,
  Grid,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Pagination } from "@mui/material";
import { NFTBox } from "./NFTPopup";

interface NftProps {
  id: number;
  url: string;
}

interface NFTAlertDialogProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  leastDestructiveRef: React.RefObject<HTMLButtonElement>;
  selectedTokens: number[];
  setSelectedTokens: React.Dispatch<React.SetStateAction<number[]>>;
  idToNft: NftProps[];
  nfts: any[];
  onClick: any;
  onOpen: () => void;
}

const NFTAlertDialog: React.FC<NFTAlertDialogProps> = ({
  children,
  isOpen,
  onClose,
  leastDestructiveRef,
  selectedTokens,
  setSelectedTokens,
  idToNft,
  onOpen,
  nfts,
  onClick,
}) => {
  const [currentPage, setCurrentPage] = React.useState<number>(1);

  const onCheck = (id: number, e?: React.ChangeEvent<HTMLInputElement>) => {
    if (id === 0) {
      if (e?.target?.checked) {
        // Select All was clicked
        if (selectedTokens.length !== idToNft.length) {
          const allNFTIds = idToNft.map((nft) => nft.id);
          setSelectedTokens(allNFTIds);
        } else {
          setSelectedTokens([]);
        }
      } else {
        // Unselect All was clicked
        setSelectedTokens([]);
      }
    } else if (selectedTokens.includes(id)) {
      setSelectedTokens(selectedTokens.filter((token) => token !== id));
    } else {
      setSelectedTokens([...selectedTokens, id]);
    }
  };

  const theme = createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#556cd6",
      },
      secondary: {
        main: "#19857b",
      },
      error: {
        main: "#ff0000",
      },
    },
  });

  return (
    <>
      <AlertDialog
        leastDestructiveRef={leastDestructiveRef}
        isOpen={isOpen}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Batch {children}</AlertDialogHeader>
            <AlertDialogCloseButton onClick={onClose} />
            <AlertDialogBody>
              <HStack>
                <Checkbox
                  checked={selectedTokens.length === nfts.length}
                  onChange={(e) => onCheck(0, e)}
                />
                <span>&nbsp;Select All</span>
              </HStack>

              <Grid templateColumns={"repeat(3, 1fr)"}>
                {idToNft
                  .slice((currentPage - 1) * 9, currentPage * 9)
                  .map((nft) => (
                    <NFTBox
                      key={nft.id}
                      image={nft.url}
                      id={nft.id}
                      checked={selectedTokens.includes(nft.id)}
                      onCheck={() => onCheck(nft.id)}
                    />
                  ))}
              </Grid>
              <HStack width={"100%"} justifyContent={"center"}>
                <ThemeProvider theme={theme}>
                  <Pagination
                    color="primary"
                    count={Math.ceil(idToNft.length / 9)}
                    page={currentPage}
                    onChange={(_, value) => {
                      setCurrentPage(value);
                    }}
                  />
                </ThemeProvider>
              </HStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <HStack
                width={"100%"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <ButtonGroup>
                  <Button
                    disabled={selectedTokens.length !== 0}
                    onClick={() => {
                      onClick();
                      onClose();
                    }}
                  >
                    Approve
                  </Button>
                  <Button ref={leastDestructiveRef} onClick={onClose}>
                    Cancel
                  </Button>
                </ButtonGroup>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Button onClick={onOpen} width={"80%"}>
        {children}
      </Button>
    </>
  );
};

export default NFTAlertDialog;
